/**
 * F-289 — Texas Route Registry (R2-A)
 * Single source of truth for all Texas nav items and routes.
 * Groups items by category. F-290/F-291 export their route entries here.
 */

import { SubchapterDChecklist } from '../components/Texas/SubchapterDChecklist';
import { RekeyCountdown } from '../components/Texas/RekeyCountdown';
import { LeadPaintDisclosure } from '../components/Texas/LeadPaintDisclosure';
import { SB38NoticeGenerator } from '../components/Texas/SB38NoticeGenerator';
import { SB38Timeline } from '../components/Texas/SB38Timeline';
import { MunicipalRegistrationsHub } from '../components/Texas/MunicipalRegistrationsHub';
import { HVACReadinessPage } from '../components/Texas/HVACReadinessPage';
import { EvidenceHub } from '../components/Texas/EvidenceHub';
import { DepositTracker } from '../components/Texas/DepositTracker';
import { TexasAnalytics } from '../components/Texas/TexasAnalytics';
import { TexasScore } from '../components/Texas/TexasScore';
import { FinanceExports } from '../components/Texas/FinanceExports';
import { StrPortfolioDashboard } from '../components/Texas/StrPortfolioDashboard';
import { TexasComplianceConsole } from '../components/Texas/TexasComplianceConsole';
import { UsdPricingAdmin } from '../components/Texas/UsdPricingAdmin';
import { TexasEventStreamOps } from '../components/Texas/TexasEventStreamOps';
import { TexasTemplateLibrary } from '../components/Texas/TexasTemplateLibrary';
import { TexasDeadlineMonitor } from '../components/Texas/TexasDeadlineMonitor';
import { NoticeVacate } from '../components/Texas/NoticeVacate';
import { SecurityScorePage } from '../components/Texas/SecurityScorePage';
import { AsecRemediationPage } from '../components/Texas/AsecRemediationPage';
import { NeedsAssessmentWizard } from '../components/Texas/needs-assessment/NeedsAssessmentWizard';
import { SmartDocumentReader } from '../components/Texas/SmartDocumentReader';
import { MultiCityComplianceDashboard } from '../components/Texas/MultiCityComplianceDashboard';
import { HRRBRegistrationsPage } from '../components/Texas/HRRBRegistrationsPage';
import { FloodComplianceDashboard } from '../components/Texas/FloodComplianceDashboard';
import { CityOverridesAdmin } from '../components/Texas/CityOverridesAdmin';
import { TexasOpsConsole } from '../components/Texas/TexasOpsConsole';
import { ScannerDashboard } from '../compliance/scanner/ScannerDashboard';
import { DsrPortal } from '../compliance/dsr/DsrPortal';
import { SecurityAuditDashboard } from '../security/audit/SecurityAuditDashboard';
import { CcpaPlaybook } from '../security/playbooks/CcpaPlaybook';
import { TxPlaybook } from '../security/playbooks/TxPlaybook';
import { LinkedInTemplateLibrary } from '../components/Growth/LinkedInTemplateLibrary';
import { EnterpriseSsoLeadForm } from '../components/Growth/EnterpriseSsoLeadForm';
import { TexasLeadQualification } from '../components/Growth/TexasLeadQualification';
import { TexasSequenceBuilder } from '../components/Growth/TexasSequenceBuilder';
import { TexasSocialScheduler } from '../components/Growth/TexasSocialScheduler';
import { TexasContentStudio } from '../components/Growth/TexasContentStudio';

export interface TexasNavItem {
    path: string;
    label: string;
    icon: string;
    group: TexasNavGroup;
    featureFlag?: string;
    component?: React.ComponentType;
}

export type TexasNavGroup = 'compliance' | 'registration' | 'safety' | 'notices' | 'other';

export const TEXAS_NAV_GROUPS: Record<TexasNavGroup, string> = {
    compliance: 'Compliance',
    registration: 'Registrations',
    safety: 'Safety & Readiness',
    notices: 'Notices',
    other: 'Other',
};

/**
 * Groups rendered as dropdowns in the Texas navbar (see TexasGroupedSidebar,
 * which reads this constant and nothing else).
 *
 * HISTORY, kept because the next reader will otherwise re-apply it: a
 * 2026-09-28 directive pinned this to ['notices'] so that every other group
 * rendered as an in-page element instead of a menu item. That directive was
 * REVERSED by the human on 2026-09-29 (four groups returned to the menu).
 * On 2026-10-06 the human removed 'other' from the menu bar again: the Other
 * group must not appear in the top menu at all. Other-group routes stay
 * registered and URL-reachable — only the menu-bar entry is removed.
 *
 * Every other group in TEXAS_NAV_GROUPS is listed; dropping one silently
 * hides live routes, so 'other' is the sole deliberate exclusion.
 */
export const TEXAS_NAV_MENU_GROUPS: TexasNavGroup[] = [
    'compliance',
    'registration',
    'safety',
    'notices',
];

export const TEXAS_ROUTES: TexasNavItem[] = [
    // Compliance group
    {
        path: '/texas/subchapter-d',
        label: 'Subchapter D Checklist',
        icon: 'fas fa-clipboard-check',
        group: 'compliance',
        featureFlag: 'HARDWARE_ENABLED',
        component: SubchapterDChecklist,
    },
    {
        path: '/texas/lead-paint',
        label: 'EPA Lead Paint Disclosure',
        icon: 'fas fa-shield-alt',
        group: 'compliance',
        featureFlag: 'LEAD_PAINT_ENABLED',
        component: LeadPaintDisclosure,
    },
    // US-158 / US-157 / US-159 / F-077 (batch-5) — US security & compliance portals.
    {
        path: '/texas/compliance-scan',
        label: 'CCPA Compliance Scan',
        icon: 'fas fa-search',
        group: 'compliance',
        component: ScannerDashboard,
    },
    {
        path: '/texas/compliance-dsr',
        label: 'Data Subject Requests',
        icon: 'fas fa-user-shield',
        group: 'compliance',
        component: DsrPortal,
    },
    {
        path: '/texas/security-audit',
        label: 'Security Audit',
        icon: 'fas fa-user-secret',
        group: 'compliance',
        component: SecurityAuditDashboard,
    },
    {
        path: '/texas/playbook-ccpa',
        label: 'Breach Playbook · CCPA',
        icon: 'fas fa-exclamation-triangle',
        group: 'compliance',
        component: CcpaPlaybook,
    },
    {
        path: '/texas/playbook-tx',
        label: 'Breach Playbook · Texas',
        icon: 'fas fa-gavel',
        group: 'compliance',
        component: TxPlaybook,
    },
    // Registration group
    {
        path: '/texas/registrations',
        label: 'Rental Registrations',
        icon: 'fas fa-clipboard-list',
        group: 'registration',
        component: MunicipalRegistrationsHub,
    },
    {
        path: '/texas/rekey',
        label: '7-Day Re-Key',
        icon: 'fas fa-door-closed',
        group: 'registration',
        component: RekeyCountdown,
    },
    // Safety & Readiness group
    {
        path: '/texas/hvac',
        label: 'HVAC Readiness',
        icon: 'fas fa-snowflake',
        group: 'safety',
        component: HVACReadinessPage,
    },
    {
        path: '/texas/deposits',
        label: 'Deposit Tracker',
        icon: 'fas fa-coins',
        group: 'safety',
        component: DepositTracker,
    },
    {
        path: '/texas/evidence',
        label: 'Evidence Hub',
        icon: 'fas fa-folder-open',
        group: 'compliance',
        component: EvidenceHub,
    },
    {
        path: '/texas/security-score',
        label: 'Security & Safety Score',
        icon: 'fas fa-shield-halved',
        group: 'safety',
        component: SecurityScorePage,
    },
    // T-163 Smart Document Reader (US-163 / F-180, Enterprise-only gate)
    {
        path: '/texas/smart-reader',
        label: 'Smart Document Reader',
        icon: 'fas fa-file-lines',
        group: 'compliance',
        component: SmartDocumentReader,
    },
    // Notices group
    {
        path: '/texas/notice-vacate',
        label: 'Notice to Vacate',
        icon: 'fas fa-file-alt',
        group: 'notices',
        component: NoticeVacate,
    },
    {
        path: '/texas/asec',
        label: 'ASEC Remediation',
        icon: 'fas fa-building',
        group: 'notices',
        component: AsecRemediationPage,
    },
    // SB 38 notices group (F-291)
    {
        path: '/texas/sb38-notice',
        label: 'SB 38 Notice Generator',
        icon: 'fas fa-file-contract',
        group: 'notices',
        component: SB38NoticeGenerator,
    },
    {
        path: '/texas/sb38-timeline',
        label: 'SB 38 Timeline Tracker',
        icon: 'fas fa-timeline',
        group: 'notices',
        component: SB38Timeline,
    },
    // F-293 Analytics & Score
    {
        path: '/texas/analytics',
        label: 'Texas Analytics',
        icon: 'fas fa-chart-bar',
        group: 'other',
        component: TexasAnalytics,
    },
    {
        path: '/texas/score',
        label: 'Texas Score',
        icon: 'fas fa-star-half-alt',
        group: 'other',
        component: TexasScore,
    },
    // F-294 Finance Exports
    {
        path: '/texas/finance',
        label: 'Finance Exports',
        icon: 'fas fa-file-export',
        group: 'other',
        component: FinanceExports,
    },
    // F-249 STR Portfolio Dashboard (E-044, US-203)
    {
        path: '/texas/str-portfolio',
        label: 'STR Portfolio',
        icon: 'fas fa-house-circle-check',
        group: 'registration',
        component: StrPortfolioDashboard,
    },
    // F-311 Texas Compliance & Privacy Console (US-256)
    {
        path: '/texas/compliance-console',
        label: 'Compliance & Privacy Console',
        icon: 'fas fa-user-shield',
        group: 'compliance',
        component: TexasComplianceConsole,
    },
// F-312 Texas Operations Console (US-257) - three additive screens.
    // Distinct paths from F-311 /texas/compliance-console per the F-312 ruling.
    {
        path: '/texas/event-stream-ops',
        label: 'Event Stream Operations',
        icon: 'fas fa-stream',
        group: 'compliance',
        component: TexasEventStreamOps,
    },
    {
        path: '/texas/template-library',
        label: 'Template Library',
        icon: 'fas fa-file-contract',
        group: 'compliance',
        component: TexasTemplateLibrary,
    },
    {
        path: '/texas/deadline-monitor',
        label: 'Deadline Monitor',
        icon: 'fas fa-clock',
        group: 'compliance',
        component: TexasDeadlineMonitor,
    },
    // F-173 USD Pricing Configuration (US-133, T-133-001/002/003)
    {
        path: '/texas/pricing-admin',
        label: 'Pricing Configuration',
        icon: 'fas fa-dollar-sign',
        group: 'other',
        component: UsdPricingAdmin,
    },
    // T-140 Needs Assessment
    {
        path: '/texas/assessment',
        label: 'Needs Assessment',
        icon: 'fas fa-clipboard-list',
        group: 'other',
        component: NeedsAssessmentWizard,
    },
    // F-146 Multi-City Texas Compliance Dashboard (T-107-001/002/003)
    {
        path: '/texas/compliance-portfolio',
        label: 'Multi-City Compliance',
        icon: 'fas fa-city',
        group: 'compliance',
        component: MultiCityComplianceDashboard,
    },
    // F-145 Houston HRRB Registration Workflow (T-106-002/003 FE half)
    {
        path: '/texas/hrrb',
        label: 'HRRB Registrations',
        icon: 'fas fa-building-shield',
        group: 'registration',
        component: HRRBRegistrationsPage,
    },
    // E-043 Flood Compliance (F-242 / F-243 / F-244 / F-245)
    {
        path: '/texas/flood',
        label: 'Flood Compliance',
        icon: 'fas fa-house-flood-water',
        group: 'compliance',
        component: FloodComplianceDashboard,
    },
    // F-301 City Override Administration (AC-1 / AC-2 / AC-3)
    {
        path: '/texas/city-overrides',
        label: 'City Overrides',
        icon: 'fas fa-scale-balanced',
        group: 'registration',
        component: CityOverridesAdmin,
    },
    // US-257 Texas Operations Console (batch-6) — run/observe ops tasks from
    // one page. Group 'other' keeps it off the top menu; URL-reachable.
    {
        path: '/texas/ops-console',
        label: 'Operations Console',
        icon: 'fas fa-toolbox',
        group: 'other',
        component: TexasOpsConsole,
    },
    // T-141-001 / T-141-002: LinkedIn Post Template Library
    {
        path: '/texas/linkedin-library',
        label: 'LinkedIn Library',
        icon: 'fab fa-linkedin',
        group: 'other',
        component: LinkedInTemplateLibrary,
    },
    // T-143-001 / T-143-002: Enterprise SSO Lead Capture & Architecture Review
    {
        path: '/texas/enterprise-sso',
        label: 'Enterprise SSO',
        icon: 'fas fa-key',
        group: 'other',
        component: EnterpriseSsoLeadForm,
    },
    // E-022 Batch-8 (F-073/F-074/F-075/F-076): sales & marketing automation.
    // Group 'other' keeps them off the top menu; URL-reachable.
    {
        path: '/texas/lead-qualification',
        label: 'Lead Qualification',
        icon: 'fas fa-ranking-star',
        group: 'other',
        component: TexasLeadQualification,
    },
    {
        path: '/texas/email-sequence',
        label: 'Email Sequence',
        icon: 'fas fa-envelope-open-text',
        group: 'other',
        component: TexasSequenceBuilder,
    },
    {
        path: '/texas/social-scheduler',
        label: 'Social Scheduler',
        icon: 'fas fa-calendar-days',
        group: 'other',
        component: TexasSocialScheduler,
    },
    {
        path: '/texas/content-studio',
        label: 'Content Studio',
        icon: 'fas fa-wand-magic-sparkles',
        group: 'other',
        component: TexasContentStudio,
    },
];

export function getTexasNavItems(group?: TexasNavGroup): TexasNavItem[] {
    if (group) return TEXAS_ROUTES.filter((item) => item.group === group);
    return TEXAS_ROUTES;
}

export function getTexasRoutePaths(): string[] {
    return TEXAS_ROUTES.map((item) => item.path);
}

export function getTexasApiGroup(item: TexasNavItem): string {
    const groupMap: Record<TexasNavGroup, string> = {
        compliance: 'complianceDashboard',
        registration: 'complianceDashboard',
        safety: 'securityScore',
        notices: 'notices',
        other: 'complianceDashboard',
    };
    return groupMap[item.group] || 'complianceDashboard';
}
